import {useState} from 'react'
import PropTypes from 'prop-types'
import {
  StyledProjectAccordion,
  StyledProjectAccordionSummary,
  StyledProjectAccordionDetails,
  AccordionTitle,
  StyledExpandIcon,
  StyledCollapseIcon,
  AccordionWrapper
} from './ProjectSectionAccordianStyles'

/**
 * ProjectSectionAccordion
 * @param {string} title - The accordion header title
 * @param {string} sectionKey - Unique identifier for this section (used for controlled state)
 * @param {boolean} expanded - Whether the accordion is expanded (controlled mode)
 * @param {function} onChange - Callback when accordion state changes
 * @param {React.ReactNode} children - The content to render inside the accordion
 * @param {React.Ref} scrollRef - Optional ref for scroll-to functionality
 * @param {string} ariaLabel - Accessibility label for the accordion
 * @param {boolean} defaultExpanded - Initial expanded state (uncontrolled mode)
 *
 */
const ProjectSectionAccordion = ({
  title,
  sectionKey,
  expanded,
  onChange,
  children,
  scrollRef,
  ariaLabel,
  defaultExpanded = false,
  ...otherProps
}) => {
  // For uncontrolled mode, we need internal state
  const [internalExpanded, setInternalExpanded] = useState(defaultExpanded)

  // Determine if we're in controlled or uncontrolled mode
  const isControlled = expanded !== undefined && onChange !== undefined

  // Get the actual expanded state
  const isExpanded = isControlled ? expanded : internalExpanded

  // Handle change for both modes
  const handleChange = (event, newExpanded) => {
    if (isControlled) {
      onChange(event, newExpanded)
    } else {
      setInternalExpanded(newExpanded)
    }
  }

  // Render the appropriate icon based on expanded state
  const renderExpandIcon = () => {
    return isExpanded ? <StyledExpandIcon /> : <StyledCollapseIcon />
  }

  return (
    <AccordionWrapper ref={scrollRef}>
      <StyledProjectAccordion
        expanded={isExpanded}
        onChange={handleChange}
        {...otherProps}
        data-testid={`accordion-${sectionKey || title.toLowerCase().replaceAll(/\s+/g, '-')}`}
      >
        <StyledProjectAccordionSummary
          expandIcon={renderExpandIcon()}
          aria-label={ariaLabel || `${title} section`}
          aria-controls={`${sectionKey || title}-content`}
          id={`${sectionKey || title}-header`}
        >
          <AccordionTitle>{title}</AccordionTitle>
        </StyledProjectAccordionSummary>
        <StyledProjectAccordionDetails>{children}</StyledProjectAccordionDetails>
      </StyledProjectAccordion>
    </AccordionWrapper>
  )
}

ProjectSectionAccordion.propTypes = {
  title: PropTypes.string.isRequired,
  sectionKey: PropTypes.string,
  expanded: PropTypes.bool,
  onChange: PropTypes.func,
  children: PropTypes.node.isRequired,
  scrollRef: PropTypes.object,
  ariaLabel: PropTypes.string,
  defaultExpanded: PropTypes.bool
}

export default ProjectSectionAccordion
